import "./ModalSelectCustomers.scss";

import { Form, Modal } from "react-bootstrap";
import { faCheck, faSearch, faTimes, faUser, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";

import AppButton from "../buttons/AppButton";
import AppInput from "../inputs/AppInput";
import AppLoading from "../AppLoading";
import CustomerAPI from "../../pages/customer/CustomerAPI";
import EmptyState from "../EmptyState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../HallAppToast";

const customerAPI = new CustomerAPI();

/**
 * Modal genérico para seleção de clientes
 * 
 * Props:
 * - show: boolean
 * - closeShow: function
 * - multiple: boolean (true = múltipla seleção com checkbox, false = seleção única)
 * - onConfirm: function(selectedCustomers) - recebe array de clientes selecionados
 * - title: string (opcional, default: "Selecionar Cliente(s)")
 * - confirmLabel: string (opcional, default: "Confirmar Seleção")
 * - preSelectedIds: array de IDs (opcional, para pré-selecionar clientes)
 */
function ModalSelectCustomers({
    show,
    closeShow,
    multiple = false,
    onConfirm,
    title,
    confirmLabel,
    preSelectedIds = [],
}) {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);

    const [isLoadingFirst, setIsLoadingFirst] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Paginação
    const [page, setPage] = useState(0);
    const limit = 20;
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const hasMore = useMemo(() => {
        if (!totalPages) return false;
        return page < totalPages - 1;
    }, [page, totalPages]);

    // Pesquisa
    const [searchValue, setSearchValue] = useState("");
    const [query, setQuery] = useState("");

    // Sentinel para infinite scroll
    const sentinelRef = useRef(null);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);

    useEffect(() => {
        loadingRef.current = isLoadingFirst || isLoadingMore;
    }, [isLoadingFirst, isLoadingMore]);

    // Reset e carrega quando query muda
    useEffect(() => {
        if (!show) return;
        resetAndLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, show]);

    // Pré-selecionar clientes
    useEffect(() => {
        if (!show || !preSelectedIds || preSelectedIds.length === 0) return;

        const preSelected = customers.filter(c => preSelectedIds.includes(c.id));
        setSelectedCustomers(preSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customers, show]);

    async function resetAndLoad() {
        setCustomers([]);
        setTotalElements(0);
        setTotalPages(0);
        setPage(0);
        setSelectedCustomers([]);

        await loadPage({ pageToLoad: 0, mode: "replace" });
    }

    async function loadPage({ pageToLoad, mode }) {
        const isFirst = pageToLoad === 0 && mode === "replace";
        try {
            if (isFirst) setIsLoadingFirst(true);
            else setIsLoadingMore(true);

            const resp = await customerAPI.getCustomersPaginated({
                page: pageToLoad,
                limit,
                q: query,
            });

            const data = resp?.data || {};
            const content = Array.isArray(data.content) ? data.content : [];

            setTotalElements(Number(data.totalElements ?? 0));
            setTotalPages(Number(data.totalPages ?? 0));
            setPage(Number(data.number ?? pageToLoad));

            setCustomers((prev) => {
                if (mode === "replace") return content;

                const existingIds = new Set(prev.map((c) => c.id));
                const next = content.filter((c) => !existingIds.has(c.id));
                return [...prev, ...next];
            });
        } catch (e) {
            HallAppToast.genericError();
        } finally {
            if (isFirst) setIsLoadingFirst(false);
            else setIsLoadingMore(false);
        }
    }

    async function loadMore() {
        if (!hasMore) return;
        if (loadingRef.current) return;

        const nextPage = page + 1;
        await loadPage({ pageToLoad: nextPage, mode: "append" });
    }

    // IntersectionObserver
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;

        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry?.isIntersecting) return;

                if (hasMore && !loadingRef.current) loadMore();
            },
            {
                root: null,
                rootMargin: "200px",
                threshold: 0.01,
            }
        );

        observerRef.current.observe(el);

        return () => observerRef.current?.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, page]);

    function handleSearchClick() {
        setQuery((searchValue || "").trim());
    }

    function handleClearSearch() {
        setSearchValue("");
        setQuery("");
    }

    function isSelected(customer) {
        return selectedCustomers.some((c) => c.id === customer.id);
    }

    function handleToggleCustomer(customer) {
        if (multiple) {
            // Múltipla seleção
            if (isSelected(customer)) {
                setSelectedCustomers((prev) => prev.filter((c) => c.id !== customer.id));
            } else {
                setSelectedCustomers((prev) => [...prev, customer]);
            }
        } else {
            // Seleção única
            setSelectedCustomers([customer]);
        }
    }

    async function handleConfirm() {
        if (selectedCustomers.length === 0) {
            HallAppToast.warning("Selecione pelo menos um cliente.");
            return;
        }

        try {
            setIsSubmitting(true);
            await onConfirm?.(selectedCustomers);
            closeShow?.();
        } catch (e) {
            HallAppToast.genericError();
        } finally {
            setIsSubmitting(false);
        }
    }

    const showEmpty = !isLoadingFirst && customers.length === 0;

    const modalTitle = title || (multiple ? "Selecionar Clientes" : "Selecionar Cliente");
    const buttonLabel = confirmLabel || `Confirmar Seleção${multiple && selectedCustomers.length > 0 ? ` (${selectedCustomers.length})` : ""}`;

    return (
        <Modal
            show={show}
            fullscreen={true}
            centered
            size="lg"
            onHide={() => closeShow?.()}
            contentClassName="hallapp-modal"
        >
            <Modal.Header closeButton className="border-bottom">
                <div className="w-100">
                    <div style={{ fontSize: 16, fontWeight: 800, lineHeight: "20px" }}>
                        {modalTitle}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                        {totalElements > 0 ? `${totalElements} cliente${totalElements !== 1 ? "s" : ""} encontrado${totalElements !== 1 ? "s" : ""}` : "Pesquise para encontrar clientes"}
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body>
                {/* Pesquisa */}
                <div className="container">
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSearchClick();
                        }}
                    >
                        <div className="customer-search">
                            <AppInput
                                label="Pesquisar (nome ou telefone)"
                                name="search"
                                errors={[]}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />

                            <div className="customer-search-actions">
                                <button
                                    type="submit"
                                    className="hallapp-search-btn"
                                    disabled={isLoadingFirst}
                                >
                                    {isLoadingFirst ? (
                                        <span className="hallapp-btn-spinner" aria-hidden="true" />
                                    ) : (
                                        <FontAwesomeIcon icon={faSearch} />
                                    )}
                                    <span>Pesquisar</span>
                                </button>

                                {(query || searchValue) && (
                                    <button
                                        type="button"
                                        onClick={handleClearSearch}
                                        className="hallapp-clear-btn"
                                        disabled={isLoadingFirst}
                                        aria-label="Limpar pesquisa"
                                        title="Limpar"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </Form>

                    {!!query && (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                            Filtrando por: <span style={{ fontWeight: 600 }}>{query}</span>
                        </div>
                    )}

                    <hr />

                    {/* Lista de clientes */}
                    <div className="customer-select-list">
                        {isLoadingFirst ? (
                            <AppLoading />
                        ) : showEmpty ? (
                            <EmptyState
                                title={query ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
                                description={
                                    query
                                        ? "Tente pesquisar com outro nome ou número."
                                        : "Cadastre clientes primeiro para poder selecioná-los."
                                }
                                icon={<FontAwesomeIcon icon={faUser} />}
                            />
                        ) : (
                            <>
                                {customers.map((customer) => {
                                    const selected = isSelected(customer);

                                    return (
                                        <div
                                            key={customer.id}
                                            className={`customer-select-item ${selected ? "customer-select-item--selected" : ""}`}
                                            onClick={() => handleToggleCustomer(customer)}
                                        >
                                            {/* Checkbox (múltipla) ou Radio visual (única) */}
                                            <div className="customer-select-checkbox">
                                                {multiple ? (
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selected}
                                                        onChange={() => { }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    <div className={`customer-select-radio ${selected ? "customer-select-radio--selected" : ""}`}>
                                                        {selected && <FontAwesomeIcon icon={faCheck} />}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Info do cliente */}
                                            <div className="customer-select-info">
                                                <div className="customer-select-name">{customer.name}</div>
                                                <div className="customer-select-phone">{customer.whatsapp || "Sem telefone"}</div>
                                            </div>

                                            {/* Ícone de selecionado */}
                                            {selected && (
                                                <div className="customer-select-icon">
                                                    <FontAwesomeIcon icon={faUserCheck} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Sentinel */}
                                <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />

                                {/* Loading mais itens */}
                                {isLoadingMore && (
                                    <div style={{ padding: "12px 0" }}>
                                        <AppLoading />
                                    </div>
                                )}

                                {/* Fim */}
                                {!isLoadingMore && !hasMore && (
                                    <div style={{ textAlign: "center", padding: "10px 0", color: "#6b7280", fontSize: 12 }}>
                                        Fim dos resultados
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-top">
                <div className="w-100 d-flex gap-2">
                    <AppButton
                        name="Cancelar"
                        width="100%"
                        onClick={closeShow}
                        disabled={isSubmitting}
                        variant="secondary"
                    />
                    <AppButton
                        name={buttonLabel}
                        width="100%"
                        onClick={handleConfirm}
                        disabled={selectedCustomers.length === 0 || isSubmitting}
                        isLoading={isSubmitting}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalSelectCustomers;